import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaTimes, FaFileAlt, FaExternalLinkAlt, FaDoorOpen, FaTrash, FaEdit, FaUser, FaCalendarPlus } from 'react-icons/fa';
import API from '../../API';

interface AgendaItem {
  id: string;
  title: string;
  time_utc: string;
  subtitle?: string;
  status?: 'upcoming' | 'ongoing' | 'completed';
  room_id?: string;
  duration_minutes?: number; // Optional duration to determine when item ends
}

interface RoomBooking {
  id: string;
  event_id: string;
  room_id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  booked_by_user_id: string;
  booked_by_username: string;
  created_at: string;
  status: string;
}

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
}

interface AgendaProps {
  accessToken: string;
  eventId: string;
  currentRoomId?: string;
  onRoomChange?: (room: Room) => void;
  cachedRooms?: Room[];
  isStaff?: boolean; // Whether the current user is Intersect staff
  currentUserId?: string; // Current user's ID for ownership check
  onRefreshBookings?: () => void; // Callback to refresh bookings
}

const Agenda: React.FC<AgendaProps> = ({ accessToken, eventId, currentRoomId, onRoomChange, cachedRooms = [], isStaff = false, currentUserId, onRefreshBookings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [bookings, setBookings] = useState<RoomBooking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedAgendaItem, setSelectedAgendaItem] = useState<AgendaItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<RoomBooking | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAgenda();
      fetchBookings();
    }
  }, [isOpen]);

  const fetchAgenda = async () => {
    setIsLoading(true);
    try {
      const response = await API.getAgenda(accessToken, eventId);
      
      if (response.status !== 200) {
        console.error('Failed to fetch agenda:', response.status);
        return;
      }
      
      const data = await response.json();
      
      // Map the response data to AgendaItem format
      const agendaItems: AgendaItem[] = Array.isArray(data) ? data : [];
      
      setAgendaItems(agendaItems);
    } catch (error) {
      console.error('Error fetching agenda:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await API.getBookings(accessToken, eventId);
      
      if (response.status !== 200) {
        console.error('Failed to fetch bookings:', response.status);
        return;
      }
      
      const data = await response.json();
      const bookingsList: RoomBooking[] = data.bookings || [];
      // Filter out cancelled bookings
      setBookings(bookingsList.filter(b => b.status !== 'cancelled'));
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleDeleteBooking = async () => {
    if (!bookingToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await API.deleteBooking(accessToken, {
        eventId: eventId,
        bookingId: bookingToDelete.id
      });
      
      if (response.status === 200) {
        // Close dialog first
        setShowDeleteConfirm(false);
        setBookingToDelete(null);
        
        // Refetch both bookings and agenda to ensure sync
        await Promise.all([fetchBookings(), fetchAgenda()]);
        
        if (onRefreshBookings) {
          onRefreshBookings();
        }
      } else {
        console.error('Failed to delete booking:', response.status);
      }
    } catch (error) {
      console.error('Error deleting booking:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoomName = (roomId: string): string => {
    const room = cachedRooms.find(r => r.room_id === roomId);
    return room?.room_name || 'Unknown Room';
  };

  const formatTime = (utcTimeString: string) => {
    const date = new Date(utcTimeString);
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes} UTC`;
  };

  // Determine actual status based on current UTC time
  const getActualStatus = (item: AgendaItem): 'upcoming' | 'ongoing' | 'completed' => {
    const now = new Date();
    const itemStartTime = new Date(item.time_utc);
    const durationMs = (item.duration_minutes || 60) * 60 * 1000; // Default 60 minutes
    const itemEndTime = new Date(itemStartTime.getTime() + durationMs);

    if (now < itemStartTime) {
      return 'upcoming';
    } else if (now >= itemStartTime && now <= itemEndTime) {
      return 'ongoing';
    } else {
      return 'completed';
    }
  };

  // Determine booking status based on current UTC time
  const getBookingStatus = (booking: RoomBooking): 'upcoming' | 'ongoing' | 'completed' => {
    const now = new Date();
    const startTime = new Date(booking.start_time);
    const endTime = new Date(booking.end_time);

    if (now < startTime) {
      return 'upcoming';
    } else if (now >= startTime && now <= endTime) {
      return 'ongoing';
    } else {
      return 'completed';
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'bg-gray-700 text-gray-400';
      case 'ongoing':
        return 'bg-green-900/30 border-green-500/50 text-green-300';
      case 'upcoming':
        return 'bg-blue-900/30 border-blue-500/50 text-blue-300';
      default:
        return 'bg-[#2a2a2a] text-white';
    }
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'ongoing':
        return 'Live Now';
      case 'upcoming':
        return 'Upcoming';
      default:
        return '';
    }
  };

  const handleAgendaItemClick = (item: AgendaItem) => {
    // Only handle click if item has a room_id and we have room change handler
    if (!item.room_id || !onRoomChange || !cachedRooms) return;
    
    // Get actual status based on UTC time
    const actualStatus = getActualStatus(item);
    
    // Only allow joining if item is ongoing
    if (actualStatus !== 'ongoing') return;
    
    // Don't navigate if already in this room
    if (item.room_id === currentRoomId) return;
    
    // Find the room from cached rooms
    const room = cachedRooms.find(r => r.room_id === item.room_id);
    if (!room) {
      console.warn('[Agenda] Room not found:', item.room_id);
      return;
    }
    
    setSelectedRoom(room);
    setSelectedAgendaItem(item);
    setShowConfirmDialog(true);
  };

  const handleBookingClick = (booking: RoomBooking) => {
    // Only handle click if booking has a room_id and we have room change handler
    if (!booking.room_id || !onRoomChange || !cachedRooms) return;
    
    // Get booking status
    const bookingStatus = getBookingStatus(booking);
    
    // Only allow joining if booking is ongoing (live)
    if (bookingStatus !== 'ongoing') return;
    
    // Don't navigate if already in this room
    if (booking.room_id === currentRoomId) return;
    
    // Find the room from cached rooms
    const room = cachedRooms.find(r => r.room_id === booking.room_id);
    if (!room) {
      console.warn('[Agenda] Room not found for booking:', booking.room_id);
      return;
    }
    
    setSelectedRoom(room);
    setSelectedAgendaItem({ 
      id: booking.id, 
      title: booking.title, 
      time_utc: booking.start_time,
      room_id: booking.room_id 
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmJoin = () => {
    if (selectedRoom && onRoomChange) {
      onRoomChange(selectedRoom);
      setShowConfirmDialog(false);
      setSelectedRoom(null);
      setSelectedAgendaItem(null);
      setIsOpen(false);
    }
  };

  const handleCancelJoin = () => {
    setShowConfirmDialog(false);
    setSelectedRoom(null);
    setSelectedAgendaItem(null);
  };

  return (
    <>
      {/* Agenda Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="px-4 py-2 bg-purple-600 bg-opacity-80 text-white rounded-lg hover:bg-opacity-100 transition-all duration-200 font-semibold flex items-center gap-2"
        title="View Agenda"
      >
        <FaCalendarAlt />
        Agenda
      </button>

      {/* Agenda Popup */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10001]"
          onClick={() => setIsOpen(false)}
        >
          <div 
            className="bg-[#1a1a1a] rounded-lg shadow-2xl border border-[#333] w-[90%] max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-[#333]">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <FaCalendarAlt className="text-purple-500" />
                Agenda
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-[#2a2a2a] rounded"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isLoading ? (
                <div className="text-center py-8 text-gray-400">
                  Loading agenda...
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Room Bookings Section */}
                  {bookings.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <FaCalendarPlus className="text-green-400" />
                        Room Bookings
                      </h3>
                      <div className="space-y-3">
                        {bookings
                          .filter((booking) => {
                            const bookingStatus = getBookingStatus(booking);
                            return bookingStatus !== 'completed';
                          })
                          .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
                          .map((booking) => {
                            const bookingStatus = getBookingStatus(booking);
                            const hasRoom = !!booking.room_id;
                            const isCurrentRoom = booking.room_id === currentRoomId;
                            const isOngoing = bookingStatus === 'ongoing';
                            const isClickable = hasRoom && onRoomChange && isOngoing && !isCurrentRoom;
                            
                            return (
                              <div
                                key={`booking-${booking.id}`}
                                onClick={() => isClickable && handleBookingClick(booking)}
                                className={`p-4 rounded-lg border transition-all duration-200 ${getStatusColor(bookingStatus)} ${
                                  isCurrentRoom ? 'ring-2 ring-green-500' : ''
                                } ${isClickable ? 'cursor-pointer hover:border-green-500 hover:shadow-lg hover:shadow-green-500/20' : ''}`}
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                      <span className="text-sm font-mono font-semibold">
                                        {formatTime(booking.start_time)}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        ({booking.duration_minutes} min)
                                      </span>
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                        isOngoing 
                                          ? 'bg-green-500 text-white animate-pulse' 
                                          : 'bg-blue-500 text-white'
                                      }`}>
                                        {getStatusLabel(bookingStatus)}
                                      </span>
                                      {hasRoom && (
                                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-600/30 text-green-300 flex items-center gap-1">
                                          <FaDoorOpen size={10} />
                                          {getRoomName(booking.room_id)}
                                        </span>
                                      )}
                                    </div>
                                    <h3 className="text-lg font-bold mb-1">
                                      {booking.title}
                                    </h3>
                                    {booking.description && (
                                      <p className="text-sm text-gray-400 mb-2">
                                        {booking.description}
                                      </p>
                                    )}
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                      <FaUser size={10} />
                                      <span>Booked by {booking.booked_by_username}</span>
                                    </div>
                                    {/* Join Room button for live bookings */}
                                    {isClickable && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleBookingClick(booking);
                                        }}
                                        className="mt-3 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center gap-2 font-semibold"
                                      >
                                        <FaDoorOpen size={12} />
                                        Join Room
                                      </button>
                                    )}
                                    {isCurrentRoom && isOngoing && (
                                      <div className="mt-3 px-4 py-2 bg-green-600/20 text-green-400 text-sm rounded-lg flex items-center gap-2 font-semibold">
                                        <FaDoorOpen size={12} />
                                        You're here
                                      </div>
                                    )}
                                  </div>
                                  {/* Staff or Owner Actions */}
                                  {(isStaff || booking.booked_by_user_id === currentUserId) && (
                                    <div className="flex gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setBookingToDelete(booking);
                                          setShowDeleteConfirm(true);
                                        }}
                                        className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded transition-colors"
                                        title="Delete Booking"
                                      >
                                        <FaTrash size={14} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Agenda Items (excluding items that have corresponding bookings) */}
                  {(() => {
                    // Get booking IDs to filter out agenda items that are auto-created from bookings
                    const bookingIds = new Set(bookings.map(b => b.id));
                    
                    // Filter out: 1) items with matching booking IDs, 2) completed items
                    const filteredAgendaItems = agendaItems
                      .filter(item => !bookingIds.has(item.id))
                      .filter(item => getActualStatus(item) !== 'completed')
                      .sort((a, b) => new Date(a.time_utc).getTime() - new Date(b.time_utc).getTime());
                    
                    // Show "no items" message only if BOTH bookings and filtered agenda items are empty
                    if (filteredAgendaItems.length === 0 && bookings.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-400">
                          No agenda items or bookings available
                        </div>
                      );
                    }
                    
                    // Only show Scheduled Events section if there are items to display
                    if (filteredAgendaItems.length === 0) {
                      return null;
                    }
                    
                    return (
                      <>
                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <FaCalendarAlt className="text-purple-400" />
                          Scheduled Events
                        </h3>
                        {filteredAgendaItems.map((item) => {
                          const actualStatus = getActualStatus(item);
                          const hasRoom = !!item.room_id;
                          const isCurrentRoom = item.room_id === currentRoomId;
                          const isOngoing = actualStatus === 'ongoing';
                          const isCompleted = actualStatus === 'completed';
                          const isClickable = hasRoom && onRoomChange && isOngoing && !isCurrentRoom;
                          
                          return (
                            <div
                              key={item.id}
                              onClick={() => isClickable && handleAgendaItemClick(item)}
                              className={`p-4 rounded-lg border transition-all duration-200 ${getStatusColor(actualStatus)} ${
                                isClickable ? 'cursor-pointer hover:bg-opacity-50 hover:border-purple-500' : ''
                              } ${isCurrentRoom ? 'ring-2 ring-green-500' : ''} ${
                                isCompleted ? 'opacity-60' : ''
                              }`}
                            >
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                                    <span className="text-sm font-mono font-semibold">
                                      {formatTime(item.time_utc)}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                      actualStatus === 'ongoing' 
                                        ? 'bg-green-500 text-white animate-pulse' 
                                        : actualStatus === 'completed'
                                          ? 'bg-gray-600 text-gray-300'
                                          : 'bg-blue-500 text-white'
                                    }`}>
                                      {getStatusLabel(actualStatus)}
                                    </span>
                                    {hasRoom && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
                                        isCurrentRoom 
                                          ? 'bg-green-500 text-white'
                                          : isOngoing
                                            ? 'bg-purple-600 text-white'
                                            : 'bg-gray-600 text-gray-400'
                                      }`}>
                                        <FaDoorOpen size={10} />
                                        {isCurrentRoom ? 'Current Room' : isOngoing ? 'Click to Join' : 'Not Available'}
                                      </span>
                                    )}
                                  </div>
                                  <h3 className="text-lg font-bold mb-1">
                                    {item.title}
                                  </h3>
                                  {item.subtitle && (
                                    <p className="text-sm text-gray-400">
                                      {item.subtitle}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-[#333] text-center">
              <p className="text-xs text-gray-500">
                All times displayed in UTC
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && selectedRoom && selectedAgendaItem && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          onClick={handleCancelJoin}
        >
          <div
            className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-xl font-semibold mb-4 flex items-center gap-2">
              <FaDoorOpen className="text-purple-500" />
              Join Room?
            </h3>
            <div className="mb-4">
              <p className="text-[#999] mb-2">
                Join <strong className="text-white">{selectedRoom.room_name}</strong> for:
              </p>
              <div className="bg-[#2a2a2a] rounded-lg p-3 border border-[#444]">
                <div className="text-sm text-gray-400 mb-1">
                  {formatTime(selectedAgendaItem.time_utc)}
                </div>
                <div className="text-white font-semibold">
                  {selectedAgendaItem.title}
                </div>
                {selectedAgendaItem.subtitle && (
                  <div className="text-sm text-gray-400 mt-1">
                    {selectedAgendaItem.subtitle}
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-[#666] mb-6">
              This will disconnect you from your current room.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelJoin}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-lg hover:bg-[#333] transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmJoin}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center gap-2"
              >
                <FaDoorOpen size={14} />
                Join Room
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Booking Confirmation Dialog */}
      {showDeleteConfirm && bookingToDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10002]"
          onClick={() => !isDeleting && setShowDeleteConfirm(false)}
        >
          <div
            className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-white text-xl font-semibold mb-4 flex items-center gap-2">
              <FaTrash className="text-red-500" />
              Delete Booking?
            </h3>
            <div className="mb-4">
              <p className="text-[#999] mb-2">
                Are you sure you want to delete this booking?
              </p>
              <div className="bg-[#2a2a2a] rounded-lg p-3 border border-[#444]">
                <div className="text-sm text-gray-400 mb-1">
                  {formatTime(bookingToDelete.start_time)} ({bookingToDelete.duration_minutes} min)
                </div>
                <div className="text-white font-semibold">
                  {bookingToDelete.title}
                </div>
                <div className="text-sm text-gray-400 mt-1">
                  Booked by {bookingToDelete.booked_by_username}
                </div>
              </div>
            </div>
            <p className="text-xs text-red-400 mb-6">
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 bg-[#2a2a2a] text-white rounded-lg hover:bg-[#333] transition-all duration-200"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBooking}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
              >
                <FaTrash size={14} />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Agenda;

